import { AxiosError } from 'axios';
import i18n from '@/common/i18n';

type ApiErrorBody = { error?: { code?: string; message?: string } };

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const EXAM_PAUSED_ADVANCE_PATTERN = /cannot advance while exam is paused/i;

const EXAM_STILL_PAUSED_KEY = 'oralExam.examStillPaused';

const EXAM_STILL_PAUSED_FALLBACK = 'The exam is still paused for another reason — resume or resolve that first, then try again.';

export function getOralExamApiErrorCode(error: unknown): string | undefined {
  const data = error instanceof AxiosError
    ? (error.response?.data as ApiErrorBody | undefined)
    : undefined;
  return data?.error?.code;
}

export function getOralExamApiErrorServerMessage(error: unknown): string | undefined {
  const data = error instanceof AxiosError
    ? (error.response?.data as ApiErrorBody | undefined)
    : undefined;
  return data?.error?.message;
}

/** True when advance was rejected because another pause is still active on the run. */
export function isOralExamPausedAdvanceError(error: unknown): boolean {
  const code = getOralExamApiErrorCode(error);
  const serverMessage = getOralExamApiErrorServerMessage(error);
  return code?.toLowerCase() === 'invalid_state_transition'
    && !!serverMessage
    && EXAM_PAUSED_ADVANCE_PATTERN.test(serverMessage);
}

function resolveTranslation(translate: TranslateFn, key: string, fallback: string): string {
  const translated = translate(key, { defaultValue: fallback });
  return translated === key ? fallback : translated;
}

/** Pull a user-facing message from an oral-exam API error, preferring i18n over fallback. */
export function getOralExamApiErrorMessage(
  error: unknown,
  fallback: string,
  t?: TranslateFn,
): string {
  const code = getOralExamApiErrorCode(error);
  if (!code) return fallback;

  const translate: TranslateFn = t ?? ((key, options) => String(i18n.t(key, options)));

  if (isOralExamPausedAdvanceError(error)) {
    return resolveTranslation(translate, EXAM_STILL_PAUSED_KEY, EXAM_STILL_PAUSED_FALLBACK);
  }

  const translationKey = `errors.${code.toLowerCase()}`;
  return resolveTranslation(translate, translationKey, fallback);
}
